import { Fragment, useState } from 'react';

import { ChevronDownIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Condition } from '@/shared/types/condition';

import { AttributePicker } from './AttributePicker';
import { ConditionChip } from './ConditionChip';
import {
  ConditionAttributeGroup,
  OPERATOR_LABEL_KEYS,
  OPERATORS,
  RULE_CONDITION_ATTRIBUTES,
} from './constants';
import {
  addConditionGroup,
  addCondition,
  ConditionItem,
  createCondition,
  removeConditionGroup,
  removeCondition,
  setConditionGroupOperator,
  setConditionOperator,
  toGroups,
  updateCondition,
} from './utils';

type Operator = Condition['operator'];

type OperatorSeparatorProps = {
  operator: Operator;
  onChange: (operator: Operator) => void;
  editable: boolean;
  hint: string;
};

const OperatorSeparator = ({ operator, onChange, editable, hint }: OperatorSeparatorProps) => {
  const { t } = useTranslation();

  if (!editable) {
    return (
      <span className="text-sm leading-none font-medium text-foreground-muted capitalize">
        {t(OPERATOR_LABEL_KEYS[operator])}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="link" size="flush" className="capitalize">
          {t(OPERATOR_LABEL_KEYS[operator])}
          <ChevronDownIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {OPERATORS.map((option) => (
          <DropdownMenuItem key={option} className="capitalize" onSelect={() => onChange(option)}>
            {t(OPERATOR_LABEL_KEYS[option])}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <p className="max-w-48 px-2 pt-1 pb-1.5 text-xs text-foreground-muted">{hint}</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

type ItemPosition = { group: number; item: number };

type ConditionFieldProps = {
  value: Condition;
  onChange: (value: Condition) => void;
  attributes?: ConditionAttributeGroup[];
  disabled?: boolean;
  showErrors?: boolean;
};

export const ConditionField = ({
  value,
  onChange,
  attributes = RULE_CONDITION_ATTRIBUTES,
  disabled = false,
  showErrors = false,
}: ConditionFieldProps) => {
  const { t } = useTranslation();
  const [newItem, setNewItem] = useState<ItemPosition | null>(null);
  const { operator, groups } = toGroups(value);

  const renderAddItem = (groupIndex: number) =>
    !disabled && (
      <AttributePicker
        groups={attributes}
        onSelect={(config) => {
          onChange(addCondition(value, groupIndex, createCondition(config)));
          setNewItem({ group: groupIndex, item: groups[groupIndex].conditions.length });
        }}
      >
        <Button variant="ghost-neutral" size="icon-sm" aria-label={t('rules.add_condition')}>
          <PlusIcon />
        </Button>
      </AttributePicker>
    );

  const renderChip = (item: ConditionItem, groupIndex: number, itemIndex: number) => (
    <ConditionChip
      item={item}
      disabled={disabled}
      showError={showErrors}
      isNew={newItem?.group === groupIndex && newItem.item === itemIndex}
      onChange={(next) => onChange(updateCondition(value, groupIndex, itemIndex, next))}
      onRemove={() => onChange(removeCondition(value, groupIndex, itemIndex))}
    />
  );

  return (
    <div className="flex flex-col items-start gap-4">
      {groups.map((group, groupIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={groupIndex}>
          {groupIndex > 0 && (
            <OperatorSeparator
              operator={operator}
              editable={groupIndex === 1 && !disabled}
              hint={t('rules.editor.operator_hint_sets')}
              onChange={(next) => onChange(setConditionOperator(value, next))}
            />
          )}
          {group.conditions.length === 1 ? (
            <div className="flex max-w-full items-center gap-2">
              {renderChip(group.conditions[0], groupIndex, 0)}
              {renderAddItem(groupIndex)}
            </div>
          ) : (
            <div className="flex w-full items-start gap-2">
              <div className="flex min-w-0 flex-1 items-start gap-2 rounded-lg border border-stroke bg-background py-1 pr-1 pl-1.5">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5 py-0.5">
                  {group.conditions.map((item, itemIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={itemIndex} className="flex flex-wrap items-center gap-1.5">
                      {renderChip(item, groupIndex, itemIndex)}
                      {itemIndex === 0 && (
                        <OperatorSeparator
                          operator={group.operator}
                          editable={!disabled}
                          hint={t('rules.editor.operator_hint_set')}
                          onChange={(next) =>
                            onChange(setConditionGroupOperator(value, groupIndex, next))
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
                {renderAddItem(groupIndex)}
              </div>
              {!disabled && (
                <InfoTooltip content={t('rules.editor.remove_set')} side="top">
                  <Button
                    variant="ghost-neutral"
                    size="icon-md"
                    className="hover:text-destructive hover:[&_svg]:text-destructive"
                    aria-label={t('rules.editor.remove_set')}
                    onClick={() => onChange(removeConditionGroup(value, groupIndex))}
                  >
                    <Trash2Icon />
                  </Button>
                </InfoTooltip>
              )}
            </div>
          )}
        </Fragment>
      ))}
      {!disabled && (
        <AttributePicker
          groups={attributes}
          onSelect={(config) => {
            onChange(addConditionGroup(value, createCondition(config)));
            setNewItem({ group: groups.length, item: 0 });
          }}
        >
          <Button variant="ghost-neutral" size="sm">
            <PlusIcon />
            {t('common.add')}
          </Button>
        </AttributePicker>
      )}
    </div>
  );
};
